import type { ProjectMilestone } from "../../../../types/project-phases.types.tsx"

export type MilestoneAmountField =
  | "planned_receivable"
  | "actual_received"
  | "planned_payable"
  | "actual_paid"

export interface ContributingMilestone {
  milestone: ProjectMilestone
  amount: number
}

/** Only the two "exceeded" rules have a milestone figure that explains an
 * overage — a mismatch can be an under-allocation, so it has no such field. */
export const AMOUNT_FIELD: Record<string, MilestoneAmountField> = {
  "contract_value_exceeded:planned": "planned_receivable",
  "contract_value_exceeded:actual": "actual_received",
  "contract_expenses_exceeded:planned": "planned_payable",
  "contract_expenses_exceeded:actual": "actual_paid"
}

/**
 * A milestone's planned payable as the planned-basis alert counts it: its
 * planned lines, else the lines booked on the Finance tab, and the typed
 * figure only for a milestone with no payable lines of its own. Reading the
 * typed figure alone would name a milestone whose stale estimate no longer
 * counts toward the overrun.
 */
export function plannedPayableAsCounted(milestone: ProjectMilestone): number {
  const plannedLines = Number(milestone.planned_paid ?? 0)
  if (plannedLines > 0) return plannedLines
  const bookedLines = Number(milestone.committed_payable ?? 0)
  if (bookedLines > 0) return bookedLines
  return Number(milestone.planned_payable ?? 0)
}

const amountOf = (
  milestone: ProjectMilestone,
  amountField: MilestoneAmountField
): number =>
  amountField === "planned_payable"
    ? plannedPayableAsCounted(milestone)
    : Number(milestone[amountField] ?? 0)

/**
 * The smallest, largest-first set of milestones whose amounts explain the
 * overage — not every milestone with a nonzero figure, and never one with
 * $0 or a negative amount (a credit line cannot be "contributing" to an
 * overrun).
 */
export function selectContributingMilestones(
  milestones: ProjectMilestone[],
  amountField: MilestoneAmountField,
  excess: number
): ContributingMilestone[] {
  if (excess <= 0) return []

  const ranked = milestones
    .map((milestone) => ({
      milestone,
      amount: amountOf(milestone, amountField)
    }))
    .filter((entry) => entry.amount > 0)
    .sort((a, b) => b.amount - a.amount)

  const selected: ContributingMilestone[] = []
  let runningTotal = 0

  for (const entry of ranked) {
    if (runningTotal >= excess) break
    selected.push(entry)
    runningTotal += entry.amount
  }

  return selected
}
